import type { Cohort } from '@efdigital/ll-addon-data';
import { selectValue } from '@efdigital/ll-addon-ui';
import SidebarContainer, {
  type NavItem,
} from '@efdigital/ll-xceed-v2/components/application/sidebar-container.gts';
import PartnershipMessagingUnreadSync from '@efdigital/ll-xceed-v2/components/coaching/messaging/partnership-messaging-unread-sync.gts';
import type { CoachingCoacheeCohortModel } from '@efdigital/ll-xceed-v2/routes/coaching/coachee/cohort';
import type CoacheeService from '@efdigital/ll-xceed-v2/services/coachee';
import type CohortAnnouncementsService from '@efdigital/ll-xceed-v2/services/cohort-announcements';
import type PartnershipMessagingService from '@efdigital/ll-xceed-v2/services/partnership-messaging';
import { cohortShowsAnnouncements } from '@efdigital/ll-xceed-v2/utils/coaching/announcements';
import { navBadge } from '@efdigital/ll-xceed-v2/utils/coaching/nav-badge';
import { cohortScopeKey } from '@efdigital/ll-xceed-v2/utils/coaching/scope-key';
import { filterCoacheeActionRequired } from '@efdigital/ll-xceed-v2/utils/coaching/session-current-categories';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { pageTitle } from 'ember-page-title';

interface CoacheeSignature {
  Args: {
    model: CoachingCoacheeCohortModel;
    controller: unknown;
  };
}

function moreThanOne(array: unknown[]) {
  return array.length > 1;
}

export default class CoacheeCohortTemplate extends Component<CoacheeSignature> {
  @service declare coachee: CoacheeService;
  @service declare cohortAnnouncements: CohortAnnouncementsService;
  @service declare partnershipMessaging: PartnershipMessagingService;
  @service declare router: RouterService;

  get nav() {
    const models = [this.args.model.program_id, this.args.model.cohort_id];
    const scopeKey = cohortScopeKey(
      this.args.model.program_id,
      this.args.model.cohort_id
    );
    void this.partnershipMessaging.unreadVersion;
    void this.cohortAnnouncements.version;
    const messagesUnreadCount =
      this.partnershipMessaging.role === 'coachee' &&
      this.partnershipMessaging.scopeKey === scopeKey
        ? this.partnershipMessaging.unreadCount
        : 0;
    const announcementsUnreadCount =
      this.cohortAnnouncements.scopeKey === scopeKey
        ? this.cohortAnnouncements.unreadCount
        : 0;
    // Derived from the planner's own session list rather than a query of its
    // own: it holds every session for the plan, it is tracked, and it reloads
    // after each booking or report, so the badge follows without a refresh.
    // coacheeSessionNeedsAction only reads status and coacheeReport, both of
    // which that list carries.
    const actionRequiredCount = filterCoacheeActionRequired(
      this.args.model.planner.allSessions
    ).length;
    const items: NavItem[] = [
      {
        link: {
          route: 'coaching.coachee.cohort.index',
          models,
        },
        icon: 'ph:squares-four',
        label: 'Dashboard',
      },
    ];

    if (cohortShowsAnnouncements(this.args.model.cohort.css)) {
      items.push({
        link: {
          route: 'coaching.coachee.cohort.announcements',
          models,
        },
        icon: 'ph:megaphone',
        label: 'Announcements',
        badge: navBadge(announcementsUnreadCount),
      });
    }

    items.push(
      {
        link: {
          route: 'coaching.coachee.cohort.quiz',
          models,
        },
        label: 'Onboarding Questionnaire',
        icon: 'ph:flask-fill',
      },
      {
        link: {
          route: 'coaching.coachee.cohort.coaches',
          models,
        },
        label: 'Coaches',
        icon: 'ph:users',
      },
      {
        link: {
          route: 'coaching.coachee.cohort.sessions',
          models,
        },
        label: 'Sessions',
        icon: 'ph:calendar',
        badge: navBadge(actionRequiredCount),
        badgeLabel: 'need attention',
      },
      {
        link: {
          route: 'coaching.coachee.cohort.goals',
          models,
        },
        label: 'Goals',
        icon: 'ph:flag',
      },
      {
        link: {
          route: 'coaching.coachee.cohort.messages',
          models,
        },
        label: 'Messages',
        icon: 'ph:chat-circle',
        badge: navBadge(messagesUnreadCount),
      },
      {
        link: {
          route: 'coaching.coachee.cohort.resources',
          models,
        },
        label: 'Resources',
        icon: 'ph:books',
      }
    );

    return items;
  }

  selectCohort = (cohortsWithCoaching: Cohort[], event: Event) => {
    const target = event.target as HTMLSelectElement;
    const cohortId = target.value;
    const cohort = cohortsWithCoaching.find(
      (cohort) => cohort.id === cohortId
    )!;
    this.router.transitionTo(
      'coaching.coachee.cohort.index',
      cohort.program.id,
      cohort.id
    );
  };

  <template>
    {{pageTitle "Coachee"}}
    {{#if @model.isCoachee}}
      <PartnershipMessagingUnreadSync
        @role="coachee"
        @programId={{@model.program_id}}
        @cohortId={{@model.cohort_id}}
        @planner={{@model.planner}}
      />

      <SidebarContainer
        @nav={{this.nav}}
        @helpRoute="coaching.coachee.cohort.help"
      >
        <:sidebar as |className|>
          {{#if (moreThanOne @model.cohortsWithCoaching)}}
            <div class={{className}}>
              <select
                class="form-select"
                {{selectValue @model.cohort_id}}
                {{on
                  "change"
                  (fn this.selectCohort @model.cohortsWithCoaching)
                }}
                aria-label="Select a cohort"
              >
                {{#each @model.cohortsWithCoaching as |cohort|}}
                  <option value={{cohort.id}}>{{cohort.title}}</option>
                {{/each}}
              </select>
            </div>
          {{/if}}
        </:sidebar>
        <:content>
          {{outlet}}
        </:content>
      </SidebarContainer>
    {{else}}
      <div class="container py-5">
        <div class="alert alert-danger" role="alert">
          You are not registered as a coachee. If you believe this is an error,
          please email
          <a
            href="mailto:v-support@hultashridge.com"
          >v-support@hultashridge.com</a>
        </div>
      </div>
    {{/if}}
  </template>
}
